import { Hash, PublicClient, TransactionReceipt, ReplacementReason, Transaction } from 'viem'

export const DEFAULT_RETRY_COUNT = 10
export const DEFAULT_RETRY_DELAY = 2000 // 2 seconds between retries

export interface WaitForTxReceiptOptions {
  /** Number of retry attempts for receipt-not-found errors */
  retryCount?: number
  /** Delay between retries in ms */
  retryDelay?: number
  /** Number of confirmations to wait for */
  confirmations?: number
  /** Polling interval in ms */
  pollingInterval?: number
  /** Callback when transaction is replaced */
  onReplaced?: (response: {
    reason: ReplacementReason
    replacedTransaction: Transaction
    transaction: Transaction
    transactionReceipt: TransactionReceipt
  }) => void
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Waits for a transaction receipt with retry logic.
 * This is necessary because some RPC providers (like Alchemy) may have indexing latency,
 * causing the receipt to not be immediately available even after the transaction is mined.
 *
 * Viem's built-in retryCount/retryDelay only handle network errors, not "receipt not found" cases.
 * This wrapper catches TransactionReceiptNotFoundError and retries manually.
 */
export async function waitForTxReceipt(
  publicClient: PublicClient,
  hash: Hash,
  options: WaitForTxReceiptOptions = {},
): Promise<TransactionReceipt> {
  const {
    retryCount = DEFAULT_RETRY_COUNT,
    retryDelay = DEFAULT_RETRY_DELAY,
    confirmations,
    pollingInterval,
    onReplaced,
  } = options

  let lastError: Error | undefined

  for (let attempt = 0; attempt <= retryCount; attempt++) {
    try {
      return await publicClient.waitForTransactionReceipt({
        hash,
        confirmations,
        pollingInterval,
        onReplaced,
      })
    } catch (error) {
      lastError = error as Error
      const isRetryable =
        lastError.name === 'TransactionReceiptNotFoundError' ||
        lastError.name === 'WaitForTransactionReceiptTimeoutError'

      if (!isRetryable) {
        throw error
      }

      if (attempt < retryCount) {
        console.warn(`Transaction receipt not found, retry ${attempt + 1}/${retryCount}...`)
        await sleep(retryDelay)
      }
    }
  }

  throw lastError
}
